import { motion } from 'framer-motion'
import { MessageSquare, Sparkles, Brain, Lightbulb } from 'lucide-react'

const SmartChat = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <MessageSquare className="mr-3 text-accent-cyan" size={32} />
        智能对话
      </h1>

      {/* 对话模式介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">聊天模式 vs 任务模式</h2>
        <p className="text-gray-400 mb-4">
          Avelia 提供两种对话模式，适用于不同的使用场景：
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5 border-l-4 border-blue-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <MessageSquare className="mr-2 text-blue-400" size={24} />
              聊天模式
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              纯对话交流，快速获取信息、回答问题、提供建议。
            </p>
            <div className="text-xs space-y-2 mb-3">
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">响应速度快</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">适合简单查询</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">不调用工具</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">使用聊天模型</span>
              </div>
            </div>
            <div className="p-3 bg-blue-500/10 rounded text-xs">
              <strong className="text-blue-300">适用场景：</strong>
              <p className="text-gray-400 mt-1">闲聊、咨询建议、知识问答、创意讨论</p>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5 border-l-4 border-purple-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Brain className="mr-2 text-purple-400" size={24} />
              任务模式
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              自动规划任务、调用工具、执行复杂操作。
            </p>
            <div className="text-xs space-y-2 mb-3">
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">自动任务规划</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">调用工具执行</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">多步骤操作</span>
              </div>
              <div className="flex items-start">
                <span className="text-green-400 mr-2">✓</span>
                <span className="text-gray-400">使用推理模型</span>
              </div>
            </div>
            <div className="p-3 bg-purple-500/10 rounded text-xs">
              <strong className="text-purple-300">适用场景：</strong>
              <p className="text-gray-400 mt-1">文件操作、数据处理、系统控制、复杂任务</p>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 glass-effect rounded-lg border border-accent-cyan/30">
          <p className="text-sm text-gray-400">
            <strong className="text-accent-cyan">💡 模式切换：</strong>
            在聊天窗口右上角，点击模式切换按钮（💬 聊天模式 / ⚡ 任务模式）即可切换。切换后会自动使用对应类型的模型。
          </p>
        </div>
      </section>

      {/* 提示词技巧 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">提示词技巧</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              明确具体
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-red-400 mb-2">❌ 不好的提问：</p>
                <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                  "帮我写个文档"
                </div>
              </div>
              <div>
                <p className="text-green-400 mb-2">✅ 好的提问：</p>
                <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                  "帮我写一份产品需求文档，包含功能描述、用户场景和技术要求，字数在1000字左右"
                </div>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              提供上下文
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-red-400 mb-2">❌ 缺少背景：</p>
                <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                  "这个方案怎么样？"
                </div>
              </div>
              <div>
                <p className="text-green-400 mb-2">✅ 提供背景：</p>
                <div className="p-3 bg-green-500/10 rounded border border-green-500/30">
                  "我在做一个电商项目，考虑使用微服务架构。这个方案的优缺点是什么？"
                </div>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              分步骤提问
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              复杂问题可以拆分成多个小问题，逐步深入：
            </p>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">第1步：</strong>
                <span className="text-gray-400 ml-2">"Python 有哪些常用的 Web 框架？"</span>
              </div>
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">第2步：</strong>
                <span className="text-gray-400 ml-2">"Flask 和 Django 的主要区别是什么？"</span>
              </div>
              <div className="p-3 bg-blue-500/10 rounded border-l-4 border-blue-500">
                <strong className="text-blue-300">第3步：</strong>
                <span className="text-gray-400 ml-2">"对于小型 API 项目，你推荐用哪个？"</span>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              指定输出格式
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              告诉 AI 你期望的回答格式：
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-purple-500/10 rounded">
                <strong className="text-purple-300">列表格式：</strong>
                <p className="text-gray-400 mt-1">"用列表形式总结要点"</p>
              </div>
              <div className="p-3 bg-purple-500/10 rounded">
                <strong className="text-purple-300">表格格式：</strong>
                <p className="text-gray-400 mt-1">"用表格对比这几个方案"</p>
              </div>
              <div className="p-3 bg-purple-500/10 rounded">
                <strong className="text-purple-300">代码格式：</strong>
                <p className="text-gray-400 mt-1">"给我一个可运行的代码示例"</p>
              </div>
              <div className="p-3 bg-purple-500/10 rounded">
                <strong className="text-purple-300">简洁回答：</strong>
                <p className="text-gray-400 mt-1">"用一句话总结"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 高级功能 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">高级功能</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">📎 文件附加</h3>
            <p className="text-sm text-gray-400 mb-3">
              支持拖拽文件到聊天窗口，AI 可以分析文件内容：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">图片：</strong>识别图片内容、提取文字、分析场景</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">文档：</strong>阅读 PDF、Word、TXT 等文档内容</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">代码：</strong>分析代码逻辑、查找问题、提供优化建议</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span><strong className="text-white">数据：</strong>处理 Excel、CSV 等数据文件</span>
              </li>
            </ul>
            <div className="mt-3 p-3 bg-yellow-500/10 rounded border border-yellow-500/30 text-xs">
              <p className="text-yellow-200">
                💡 <strong>提示：</strong>单次最多可附加 10 个文件。文件会保留在输入框中，方便多轮对话引用。
              </p>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🎤 语音输入</h3>
            <p className="text-sm text-gray-400 mb-3">
              点击输入框右侧的麦克风图标，使用语音输入：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>支持实时语音识别，边说边转文字</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>识别完成后自动填入输入框</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>可以继续编辑识别结果</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🧠 深度思考模式</h3>
            <p className="text-sm text-gray-400 mb-3">
              在任务模式下，可以开启"深度思考"开关：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>AI 会进行更深入的分析和推理</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>展示完整的思考过程</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>适合复杂问题和重要决策</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 使用建议 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-green-500/30">
        <h3 className="text-xl font-semibold mb-3 flex items-center">
          <Lightbulb className="mr-2 text-green-400" size={24} />
          对话建议
        </h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">保持礼貌：</strong>虽然是 AI，但友好的交流会让对话更愉快</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">及时反馈：</strong>如果回答不满意，可以说"请重新回答"或"换个角度"</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">引用上文：</strong>可以说"基于刚才的回答"来延续话题</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">善用知识库：</strong>提问时明确提及"查询知识库"或"根据文档"，AI 会检索你导入的资料</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">自定义指令：</strong>在"指令管理"中添加常用的角色设定或对话风格</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default SmartChat
